import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { saveAs } from "file-saver";
import { useAuth } from "../context/AuthContext";
import "../pages/ClientList.css";

function ClientList() {
  const { currentUser: user, isAdmin, isSuperAdmin } = useAuth();

  // State for client data & editing
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedClient, setEditedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");

  // Updated order: clientID after name, defaultContact after shippingAddress, bellNotes after notes
  const fieldOrder = [
    "name",
    "clientID",
    "clientType",
    "address",
    "shippingAddress",
    "defaultContact",
    "contact1", "contact1Title", "contact1Phone", "contact1Email",
    "contact2", "contact2Title", "contact2Phone", "contact2Email",
    "schoolComps",
    "coachComps",
    "notes",
    "bellNotes",
    "dropbox",
  ];

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "clients"));
        const clientList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          shippingAddress: doc.data().shippingAddress || "",
          defaultContact: doc.data().defaultContact || ""
        }));
        clientList.sort((a, b) => a.name.localeCompare(b.name));
        setClients(clientList);
        setFilteredClients(clientList);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    let filtered = clients.filter(client =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filterType) {
      filtered = filtered.filter(client => client.clientType === filterType);
    }
    setFilteredClients(filtered);
  }, [searchTerm, filterType, clients]);

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setEditedClient({
      id: selectedClient.id,
      name: selectedClient.name || "",
      clientID: selectedClient.clientID || "",
      clientType: selectedClient.clientType || "",
      address: selectedClient.address || "",
      shippingAddress: selectedClient.shippingAddress || "",
      defaultContact: selectedClient.defaultContact || "",
      contact1: selectedClient.contact1 || "",
      contact1Title: selectedClient.contact1Title || "",
      contact1Phone: selectedClient.contact1Phone || "",
      contact1Email: selectedClient.contact1Email || "",
      contact2: selectedClient.contact2 || "",
      contact2Title: selectedClient.contact2Title || "",
      contact2Phone: selectedClient.contact2Phone || "",
      contact2Email: selectedClient.contact2Email || "",
      schoolComps: selectedClient.schoolComps || "",
      coachComps: selectedClient.coachComps || "",
      notes: selectedClient.notes || "",
      bellNotes: selectedClient.bellNotes || "",
      dropbox: selectedClient.dropbox || ""
    });
    setIsEditing(true);
  };

  const handleSaveChanges = async () => {
    if (!editedClient) return;
    try {
      const clientRef = doc(db, "clients", editedClient.id);
      await updateDoc(clientRef, editedClient);
      setSelectedClient(editedClient);
      setIsEditing(false);
      setClients(prev =>
        prev.map(c => (c.id === editedClient.id ? editedClient : c))
      );
      alert("Client details updated successfully!");
    } catch (error) {
      console.error("Error updating client:", error);
      alert("Failed to update client.");
    }
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedClient.name}?`)) return;
    try {
      await deleteDoc(doc(db, "clients", selectedClient.id));
      alert("Client deleted successfully!");
      setClients(prev => prev.filter(c => c.id !== selectedClient.id));
      setFilteredClients(prev => prev.filter(c => c.id !== selectedClient.id));
      setSelectedClient(null);
    } catch (error) {
      console.error("Error deleting client:", error);
      alert("Failed to delete client.");
    }
  };

  const handleExport = () => {
    if (filteredClients.length === 0) {
      alert("No clients to export!");
      return;
    }
    const csvHeader =
      "Name,Client ID,Client Type,Address,Shipping Address,Default Contact,Contact 1,Contact 1 Title,Contact 1 Phone,Contact 1 Email,Contact 2,Contact 2 Title,Contact 2 Phone,Contact 2 Email,School Comps,Coach Comps,Notes,Bell Notes\n";
    const csvRows = filteredClients.map(client =>
      `"${client.name}","${client.clientID || ""}","${client.clientType}","${client.address}","${client.shippingAddress}","${client.defaultContact || ""}","${client.contact1}","${client.contact1Title}","${client.contact1Phone}","${client.contact1Email}","${client.contact2 || ""}","${client.contact2Title || ""}","${client.contact2Phone || ""}","${client.contact2Email || ""}","${client.schoolComps}","${client.coachComps}","${client.notes}","${client.bellNotes || ""}"`
    );
    const blob = new Blob([csvHeader + csvRows.join("\n")], { type: "text/csv" });
    saveAs(blob, "clients.csv");
  };

  return (
    <div className="client-list-container">
      <div className="left-panel">
        <h2>Clients</h2>
        <input
          type="text"
          placeholder="Search clients..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <select onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          <option value="High School">High School</option>
          <option value="Middle School">Middle School</option>
          <option value="Baseball/Softball Club">Baseball/Softball Club</option>
          <option value="Dance/Gymnastics Club">Dance/Gymnastics Club</option>
          <option value="Event">Event</option>
          <option value="Football/Cheer Club">Football/Cheer Club - Regular pricing</option>
          <option value="Headshots/Corporate">Headshots/Corporate</option>
          <option value="Hockey/Figure Skating">Hockey/Figure Skating</option>
          <option value="League">League Large-Youth Pricing</option>
          <option value="Miscellaneous">Miscellaneous</option>
          <option value="Swim Club">Swim/Tennis Club</option>
          <option value="Travel Team (Small Single Team)">Travel Team (Small Single Team)</option>
          <option value="Volleyball Club">Volleyball Club</option>
          <option value="Wrestling Club">Wrestling Club</option>
        </select>
        <button className="export-button" onClick={handleExport}>Export to CSV</button>
        <ul>
          {filteredClients.map(client => (
            <li
              key={client.id}
              className="client-item"
              onClick={() => handleClientClick(client)}
            >
              {client.name}
            </li>
          ))}
        </ul>
      </div>
      <div className="right-panel">
        {selectedClient ? (
          <div className="client-details">
            {isEditing ? (
              <div className="edit-container">
                <h2>Edit Client</h2>
                {fieldOrder.map(field => (
                  (editedClient[field] !== undefined) && (
                    <div key={field} className="section-divider">
                      <label>{field.replace(/([A-Z])/g, ' $1').trim()}:</label>
                      {field === "notes" || field === "bellNotes" ? (
                        <textarea
                          value={editedClient[field]}
                          onChange={e =>
                            setEditedClient({ ...editedClient, [field]: e.target.value })
                          }
                        />
                      ) : field === "clientType" ? (
                        <select
                          value={editedClient[field]}
                          onChange={e =>
                            setEditedClient({ ...editedClient, [field]: e.target.value })
                          }
                        >
                          <option value="High School">High School</option>
                          <option value="Middle School">Middle School</option>
                          <option value="Baseball/Softball Club">Baseball/Softball Club</option>
                          <option value="Dance/Gymnastics Club">Dance/Gymnastics Club</option>
                          <option value="Event">Event</option>
                          <option value="Football/Cheer Club">Football/Cheer Club - Regular pricing</option>
                          <option value="Headshots/Corporate">Headshots/Corporate</option>
                          <option value="Hockey/Figure Skating">Hockey/Figure Skating</option>
                          <option value="League">League Large-Youth Pricing</option>
                          <option value="Miscellaneous">Miscellaneous</option>
                          <option value="Swim Club">Swim/Tennis Club</option>
                          <option value="Travel Team (Small Single Team)">Travel Team (Small Single Team)</option>
                          <option value="Volleyball Club">Volleyball Club</option>
                          <option value="Wrestling Club">Wrestling Club</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={editedClient[field]}
                          onChange={e =>
                            setEditedClient({ ...editedClient, [field]: e.target.value })
                          }
                        />
                      )}
                    </div>
                  )
                ))}
                <div className="edit-buttons">
                  <button className="save-btn" onClick={handleSaveChanges}>Save</button>
                  <button className="cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <h2>{selectedClient.name}</h2>
                {fieldOrder.map(field => (
                  (selectedClient[field] !== undefined) && (
                    <div key={field} className="section-divider">
                      <h3>{field.replace(/([A-Z])/g, ' $1').trim()}:</h3>
                      {field === "notes" || field === "bellNotes" ? (
                        <div className="notes-container">
                          <p>{selectedClient[field]}</p>
                        </div>
                      ) : field === "dropbox" ? (
                        <p>
                          {selectedClient.dropbox
                            ? <a href={selectedClient.dropbox} target="_blank" rel="noopener noreferrer">{selectedClient.dropbox}</a>
                            : "N/A"}
                        </p>
                      ) : (
                        <p>{selectedClient[field]}</p>
                      )}
                    </div>
                  )
                ))}
                {isAdmin && (
                  <div style={{ marginTop: 10 }}>
                    <button onClick={handleEditClick} className="btn btn-primary">Edit</button>
                    {isSuperAdmin && (
                      <button onClick={handleDeleteClient} className="btn btn-danger" style={{ marginLeft: 8 }}>
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <h2>Select a client to view details</h2>
        )}
      </div>
    </div>
  );
}

export default ClientList;
